import type {
    NodeData,
    TextBoxViewModel,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    ViewViewModel,
    WindowViewModel,
    LabelViewModel,
    ButtonViewModel,
    ImageViewModel,
    LinkViewModel,
    GridViewModel,
    ToolBarViewModel,
    CheckBoxViewModel,
    DropDownListViewModel,
    Utilities,
    TreeUtilities,
    RADApplication,
    GlobalConfiguration as Config,
    GlobalConfiguration,
    NavigationService,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type { PropertyGroupDropDownData } from '@/Datastructures';

class EditPropertyGroupViewViewModel extends ViewViewModel {
    // html bindings
    public okay: ButtonViewModel;
    public cancel: ButtonViewModel;
    private window: WindowViewModel;
    private isVisibleCheckbox: CheckBoxViewModel;
    private isVisibleLabel: LabelViewModel;
    private uiGeneratorLabel: LabelViewModel;
    private uiGenerator: DropDownListViewModel;
    private parentGroupLabel: LabelViewModel;
    private parentGroup: DropDownListViewModel;
    private layoutLabel: LabelViewModel;
    private layout: DropDownListViewModel;
    private collapsedLabel: LabelViewModel;
    private collapsed: CheckBoxViewModel;
    private showBrowseButtonLabel: LabelViewModel;
    private showBrowseButton: CheckBoxViewModel;
    private moveDown: LinkViewModel;
    private moveUp: LinkViewModel;
    private changeOrderLabel: LabelViewModel;
    private toolBar: ToolBarViewModel;
    private grid: GridViewModel;

    // class-properties
    private currentNode: NodeData;

    // by params
    private callback: (
        propertyGruppenData: Array<NodeData>,
        isVisible: boolean,
        uiGenerator: string,
        parentGroup: string
    ) => void;
    private isVisible: boolean;
    private uiGeneratorString: string;
    private currentParentKey: string;
    private parentGroupData: Array<{ caption: string; value: string }>;
    private propertyGruppenData: Array<NodeData>;
    private selectedGroupPdoId: string;

    private currentLanguage: string;

    constructor(params: any) {
        super(params);

        this.currentLanguage = RADApplication.getInstance().currentLanguage();

        this.callback = params.callback;
        this.isVisible = params.isVisible;
        this.uiGeneratorString = params.uiGenerator;
        this.currentParentKey = params.currentParentGroup; // key of the PropertyGroup that is parent of the currently selected PropertyGroup
        this.parentGroupData = params.parentGroupData; // array of PropertyGroups that can be selected as new Parent of the currently selected PropertyGroup
        this.propertyGruppenData = params.propertyGruppenData; // array of NodeData that is used to fill the SortPropertiesGrid
        this.selectedGroupPdoId = params.selectedGroupPdoId; // pdoId of the currently selected PropertyGroup

        this.currentNode = TreeUtilities.getNodeByKey(
            this.propertyGruppenData,
            this.selectedGroupPdoId
        );

        this.viewFactory = params.viewFactory;

        this.createIsVisibleCheckbox();
        this.createUiGeneratorDropDown();
        this.createLayoutDropDown();
        this.createCollapsedCheckbox();
        this.createShowBrowseButtonCheckbox();

        this.createSortPropertiesGrid();
        this.createDropDownForParentPropertyGroup();
        this.createWindow();
        this.createOkayButton();
        this.createCancelButton();

        this.syncMovePropertyLinksEnabledStates();
        this.syncGridDataWithParentSelection();

        // propertyGruppenData shouldn't be disposed due to sub-context which provides the data for this window
        this.undisposeProperties.push('propertyGruppenData');
    }

    private createIsVisibleCheckbox() {
        this.isVisibleCheckbox = new CheckBoxViewModel({
            isChecked: this.isVisible,
            locator: this.locator() + '.visible',
        });
        this.isVisibleLabel = new LabelViewModel({ caption: 'Sichtbar' });
    }

    private createUiGeneratorDropDown() {
        let uiGeneratorData = [
            this.makeOption('ExpanderGroup', 'ExpanderGroup'),
            this.makeOption('TabGroup', 'TabGroup'),
            this.makeOption('WizardGroup', 'WizardGroup'),
        ];

        this.uiGeneratorLabel = new LabelViewModel({ caption: 'UI-Generator' });
        this.uiGenerator = new DropDownListViewModel({
            data: uiGeneratorData,
            dataTextField: 'caption',
            dataValueField: 'value',
            isEnabled: true,
            locator: this.locator() + '.uiGenerator',
        });

        let predicate = (option: UiGeneratorOption) =>
            Utilities.startsWith(this.uiGeneratorString, option.value);
        let matchInOptions = Utilities.findFirst(
            this.uiGenerator.data(),
            predicate
        ) as UiGeneratorOption;

        if (matchInOptions) {
            this.uiGenerator.value(matchInOptions.value);
        }
    }

    private createLayoutDropDown() {
        let layoutData = [
            this.makeOption('OneColumnLayout', 'OneColumnLayout'),
            this.makeOption('TwoColumnLayout', 'TwoColumnLayout'),
            this.makeOption('AutoTwoColumnLayout', 'AutoTwoColumnLayout'),
            this.makeOption('FlowLayout', 'FlowLayout'),
        ];

        this.layoutLabel = new LabelViewModel({ caption: 'Layout' });
        this.layout = new DropDownListViewModel({
            data: layoutData,
            dataTextField: 'caption',
            dataValueField: 'value',
            isEnabled: true,
            locator: this.locator() + '.layout',
        });

        let predicate = (option: UiGeneratorOption) =>
            Utilities.contains(
                this.uiGeneratorString,
                'Layout=' + option.value
            );
        let layoutMatch = Utilities.findFirst(
            this.layout.data(),
            predicate
        ) as UiGeneratorOption;

        if (layoutMatch) {
            this.layout.value(layoutMatch.value);
        }
    }

    private createCollapsedCheckbox() {
        this.collapsedLabel = LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_COLLAPSED'
        );
        this.collapsed = new CheckBoxViewModel({
            isChecked: Utilities.contains(
                this.uiGeneratorString,
                'IsCollapsed=true'
            ),
            isEnabled: this.defineComputed(() => {
                return this.uiGenerator.value() == 'ExpanderGroup';
            }),
            locator: this.locator() + '.collapsed',
        });
    }

    private createShowBrowseButtonCheckbox() {
        this.showBrowseButtonLabel = LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_BROWSEBUTTONS'
        );
        this.showBrowseButton = new CheckBoxViewModel({
            isChecked: Utilities.contains(
                this.uiGeneratorString,
                'ShowBrowseButton=true'
            ),
            isEnabled: this.defineComputed(() => {
                return this.uiGenerator.value() == 'WizardGroup';
            }),
            locator: this.locator() + '.show_browse_button',
        });
    }

    private createSortPropertiesGrid() {
        this.createSortPropertiesGrid_Label();
        this.createSortPropertiesGrid_MoveUpLink();
        this.createSortPropertiesGrid_MoveDownLink();
        this.createSortPropertiesGrid_Toolbar();
        this.createSortPropertiesGrid_Grid();
    }

    private createDropDownForParentPropertyGroup() {
        let noParentOption = { caption: '', value: null };
        let data = [noParentOption].concat(this.parentGroupData);

        this.parentGroupLabel = LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_PARENT'
        );
        this.parentGroup = new DropDownListViewModel({
            data: data,
            dataTextField: 'caption',
            dataValueField: 'value',
            isEnabled: true,
            locator: this.locator() + '.prop_group_parent',
        });
        this.parentGroup.value(this.currentParentKey);
    }

    private createWindow() {
        this.window = new WindowViewModel({
            locator: this.locator() + '.window',
            isOpen: true,
            width: 600,
            isModal: true,
            allowClose: false,
            headline: new LabelViewModel({
                caption: this.currentNode.title,
            }),
            onClose: () => {
                NavigationService.getInstance().removeViewFactoryIsolated(
                    this.viewFactory
                );
            },
        });
    }

    private createOkayButton() {
        this.okay = new ButtonViewModel({
            locator: this.locator() + '.button.okay',
            image: new ImageViewModel({
                locator: this.locator() + '.button.okay.image',
                source: GlobalConfiguration.inlineImages.WINDOW_OK,
                alternativeText: new LabelViewModel({
                    ergName: 'OKAY',
                }),
            }),
            label: new LabelViewModel({
                locator: this.locator() + '.button.okay.label',
                ergName: 'OKAY',
            }),
            onClick: () => {
                this.buildUIGeneratorString();
                this.callback(
                    this.propertyGruppenData,
                    this.isVisibleCheckbox.isChecked(),
                    this.uiGeneratorString,
                    this.parentGroup.value()
                );
                this.window.isOpen(false);
            },
        });
    }

    private createCancelButton() {
        this.cancel = new ButtonViewModel({
            locator: this.locator() + '.button.cancel',
            image: new ImageViewModel({
                locator: this.locator() + '.button.cancel.image',
                source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                alternativeText: new LabelViewModel({
                    ergName: 'CANCEL',
                }),
            }),
            label: new LabelViewModel({
                locator: this.locator() + '.button.cancel.label',
                ergName: 'CANCEL',
            }),
            onClick: () => {
                this.window.isOpen(false);
            },
        });
    }

    private createSortPropertiesGrid_Label() {
        this.changeOrderLabel = LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_CHANGEORDER'
        );
    }

    private createSortPropertiesGrid_MoveUpLink() {
        this.moveUp = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.MOVE_UP,
                alternativeText: new LabelViewModel({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_MOVE_UP',
                }),
            }),
            tooltip: LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_MOVE_UP'
            ),
            isEnabled: false,
            isVisible: true,
            locator: this.locator() + '.move_up',
            onClick: () => {
                let selected = this.grid.selected()[0];
                if (!selected) {
                    return;
                }

                let allRows = this.grid.data();
                let index = _.findIndex(allRows, e => e['pdoId'] == selected);
                if (index >= 1) {
                    Utilities.swap(allRows, index, index - 1);
                    this.grid.data.valueHasMutated();
                    this.moveDown.isEnabled(true);
                    this.moveUp.isEnabled(index > 1);

                    let currentParent =
                        this.parentGroup.value() != null
                            ? TreeUtilities.getNodeByKey(
                                  this.propertyGruppenData,
                                  this.parentGroup.value()
                              )
                            : null;
                    let currentChildren =
                        currentParent != null
                            ? currentParent.children
                            : this.propertyGruppenData;

                    Utilities.swap(currentChildren, index, index - 1);
                }
            },
        });
    }

    private createSortPropertiesGrid_MoveDownLink() {
        this.moveDown = new LinkViewModel({
            image: new ImageViewModel({
                source: Config.inlineImages.MOVE_DOWN,
                alternativeText: new LabelViewModel({
                    ergName:
                        'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_MOVE_DOWN',
                }),
            }),
            tooltip: LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUP_MOVE_DOWN'
            ),
            isEnabled: false,
            isVisible: true,
            locator: this.locator() + '.move_down',
            onClick: () => {
                let selected = this.grid.selected()[0];
                if (!selected) {
                    return;
                }

                let allRows = this.grid.data();
                let index = _.findIndex(allRows, e => e['pdoId'] == selected);
                if (index + 1 < allRows.length) {
                    Utilities.swap(allRows, index, index + 1);
                    this.grid.data.valueHasMutated();
                    this.moveUp.isEnabled(true);
                    this.moveDown.isEnabled(index + 2 < allRows.length);

                    let currentParent =
                        this.parentGroup.value() != null
                            ? TreeUtilities.getNodeByKey(
                                  this.propertyGruppenData,
                                  this.parentGroup.value()
                              )
                            : null;
                    let currentChildren =
                        currentParent != null
                            ? currentParent.children
                            : this.propertyGruppenData;

                    Utilities.swap(currentChildren, index, index + 1);
                }
            },
        });
    }

    private createSortPropertiesGrid_Toolbar() {
        this.toolBar = new ToolBarViewModel({
            primary: [],
            secondary: [this.moveUp, this.moveDown],
        });
    }

    private createSortPropertiesGrid_Grid() {
        this.grid = new GridViewModel({
            columnDefinition: this.getColumnDefinition(),
            isMultiColumnSelect: false,
            identity: 'pdoId',
            data: [],
            height: '200px',
            locator: this.locator() + '.sort_props_grid',
        });
    }

    private syncMovePropertyLinksEnabledStates() {
        this.defineSubscription(this.grid.selected, newSelection => {
            let selected = newSelection[0];
            let data = this.grid.data();
            let test = anyData => anyData.pdoId == selected;
            let index = selected ? _.findIndex(data, test) : -1;

            this.moveUp.isEnabled(index > 0);
            this.moveDown.isEnabled(index >= 0 && index < data.length - 1);
        });
    }

    private syncGridDataWithParentSelection() {
        this.defineSubscription(
            this.parentGroup.value,
            (newParentNodeKey: string) => {
                let newParentNode = TreeUtilities.getNodeByKey(
                    this.propertyGruppenData,
                    newParentNodeKey
                );
                let oldParentNode = TreeUtilities.getNodeByKey(
                    this.propertyGruppenData,
                    this.currentParentKey
                );

                // re-arrange tree-hierarchy if old and new parentNodes are un-equal
                if (newParentNodeKey != this.currentParentKey) {
                    TreeUtilities.removeNode(
                        this.propertyGruppenData,
                        this.currentNode
                    );
                    TreeUtilities.insertNode(
                        this.propertyGruppenData,
                        this.currentNode,
                        newParentNode
                    );
                    if (newParentNode != null) {
                        newParentNode.expanded = true;
                    }
                }

                // update grid data
                let dataUpdateNodes =
                    newParentNode != null
                        ? newParentNode.children
                        : this.propertyGruppenData;
                let dataUpdate = dataUpdateNodes.map(anyNode =>
                    this.toPropertyGroupOption(anyNode)
                );
                this.grid.data(dataUpdate);
                this.grid.selected([]);

                // update currentParentKey
                if (this.currentParentKey != newParentNodeKey) {
                    this.currentParentKey = newParentNodeKey;
                }
            }
        )(this.parentGroup.value());
    }

    private toPropertyGroupOption(node: NodeData): PropertyGroupDropDownData {
        return {
            pdoId: node.key,
            objectIdentValue: node.title,
        };
    }

    private buildUIGeneratorString() {
        let uiGenerator = this.uiGenerator.value();

        let isTabGroup = uiGenerator == 'TabGroup';
        let isExpanderGroup = uiGenerator == 'ExpanderGroup';
        let isWizardGroup = uiGenerator == 'WizardGroup';

        this.uiGeneratorString = uiGenerator;

        let params = [];

        if (Utilities.isNotBlank(this.layout.value())) {
            params.push('Layout=' + this.layout.value());
        }
        if (isExpanderGroup) {
            params.push(
                'IsCollapsed=' + (this.collapsed.isChecked() ? 'true' : 'false')
            );
        }
        if (isWizardGroup) {
            // TODO no evidence for how this parameter has to look like
            params.push(
                'ShowBrowseButton=' +
                    (this.showBrowseButton.isChecked() ? 'true' : 'false')
            );
        }

        if (!_.isEmpty(params)) {
            this.uiGeneratorString += '?' + params.join('&');
        }
    }

    private getColumnDefinition() {
        let visible = true;

        let nameColumnDefinition = GridViewModel.createColumnDefinition(
            visible,
            'RADEM.RADEXTENSIONMANAGER.COLUMN_NAME',
            'objectIdentValue'
        );
        nameColumnDefinition['width'] = '100%';

        let idColumnDefinition = GridViewModel.createColumnDefinition(
            !visible,
            'RADEM.RADEXTENSIONMANAGER.COLUMN_ID',
            'pdoId'
        );
        idColumnDefinition['width'] = '0%';

        return [idColumnDefinition, nameColumnDefinition];
    }

    private makeOption(caption, value) {
        return {
            caption: caption,
            value: value,
        };
    }
}

interface UiGeneratorOption {
    caption: string;
    value: string;
}

export default EditPropertyGroupViewViewModel;
